<template>
  <div class="tw-bg-gray-100">
    <AppBar />
    <div
      class="tw-relative tw-isolate tw-bg-white tw-px-6 tw-py-24 sm:tw-py-32 lg:tw-px-8"
    >
      <div
        class="tw-absolute tw-inset-x-0 -top-3 -z-10 tw-transform-gpu tw-overflow-hidden tw-px-36 tw-blur-3xl"
        aria-hidden="true"
      >
        <div
          class="tw-mx-auto tw-aspect-1155/678 tw-w-[72.1875rem] tw-bg-linear-to-tr tw-from-[#ff80b5] tw-to-[#9089fc] tw-opacity-30"
          style="
            clip-path: polygon(
              74.1% 44.1%,
              100% 61.6%,
              97.5% 26.9%,
              85.5% 0.1%,
              80.7% 2%,
              72.5% 32.5%,
              60.2% 62.4%,
              52.4% 68.1%,
              47.5% 58.3%,
              45.2% 34.5%,
              27.5% 76.7%,
              0.1% 64.9%,
              17.9% 100%,
              27.6% 76.8%,
              76.1% 97.7%,
              74.1% 44.1%
            );
          "
        />
      </div>
      <div class="tw-mx-auto tw-max-w-4xl tw-text-center">
        <h2 class="tw-text-4xl tw-font-semibold tw-text-green-500">Plans</h2>
        <p
          class="tw-mt-2 tw-text-5xl tw-font-semibold tw-tracking-tight tw-text-balance tw-text-green-500 sm:tw-text-6xl"
        >
          Choose the right plan for you
        </p>
      </div>
      <p
        class="tw-mx-auto tw-mt-6 tw-max-w-2xl tw-text-center tw-text-lg tw-font-medium tw-text-pretty tw-text-green-600 sm:tw-text-xl/8"
      >
        Choose an affordable plan that's packed with the best features for
        engaging your audience, creating customer loyalty, and driving sales.
      </p>

      <div v-if="isLoading" class="tw-w-[550px] tw-mx-auto tw-mt-16">
        <v-progress-linear indeterminate color="green" />
      </div>
      <div
        v-else
        class="tw-mx-auto tw-mt-16 tw-grid tw-max-w-lg tw-grid-cols-1 tw-items-center tw-gap-y-6 sm:tw-mt-20 sm:tw-gap-y-0 lg:tw-max-w-4xl lg:tw-grid-cols-3"
      >
        <div
          v-for="(tier, tierIdx) in tiers"
          :key="tier.id"
          :class="[
            tier.featured
              ? 'tw-relative tw-bg-green-900 tw-shadow-2xl'
              : 'tw-bg-white/60 sm:tw-mx-8 lg:tw-mx-0',
            tier.featured
              ? ''
              : tierIdx === 0
              ? 'tw-rounded-t-3xl sm:tw-rounded-b-none lg:tw-rounded-tr-none lg:tw-rounded-bl-3xl'
              : 'sm:tw-rounded-t-none lg:tw-rounded-tr-3xl lg:tw-rounded-bl-none',
            'tw-rounded-3xl tw-p-8 tw-ring-1 tw-ring-gray-900/10 sm:tw-p-10',
          ]"
        >
          <h3
            :id="tier.id"
            :class="[
              tier.featured ? 'tw-text-green-500' : 'tw-text-green-600',
              'tw-text-base/7 tw-font-semibold',
            ]"
          >
            {{ tier.name }}
          </h3>
          <p class="tw-mt-4 tw-flex tw-items-baseline tw-gap-x-2">
            <span
              :class="[
                tier.featured ? 'tw-text-white' : 'tw-text-gray-900',
                'tw-text-5xl tw-font-semibold tw-tracking-tight',
              ]"
              >{{ tier.priceMonthly }}</span
            >
            <span
              :class="[
                tier.featured ? 'tw-text-gray-400' : 'tw-text-gray-500',
                'tw-text-base',
              ]"
              >/month</span
            >
          </p>
          <!-- <p
            :class="[
              tier.featured ? 'tw-text-gray-300' : 'tw-text-gray-600',
              'tw-mt-6 tw-text-base/7',
            ]"
          >
            {{ tier.description }}
          </p> -->
          <ul
            role="list"
            :class="[
              tier.featured ? 'tw-text-gray-300' : 'tw-text-gray-600',
              'tw-mt-8 tw-space-y-3 tw-text-sm/6 sm:tw-mt-10',
            ]"
          >
            <li
              v-for="feature in tier.features"
              :key="feature"
              class="tw-flex tw-gap-x-3"
            >
              <CheckIcon
                :class="[
                  tier.featured ? 'tw-text-green-400' : 'tw-text-green-600',
                  'tw-h-6 tw-w-5 tw-flex-none',
                ]"
                aria-hidden="true"
              />
              {{ feature }}
            </li>
          </ul>
          <router-link
            :to="tier.href"
            :aria-describedby="tier.id"
            @click="!tier.is_coming && onPlanSelect(tier)"
            :class="[
              'tw-mt-8 tw-block tw-rounded-md tw-px-3.5 tw-py-2.5 tw-text-center tw-text-sm tw-font-semibold focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 sm:tw-mt-10',
              tier.is_coming
                ? 'tw-bg-gray-200 tw-text-gray-400 tw-cursor-not-allowed focus-visible:tw-outline-gray-400'
                : tier.featured
                ? 'tw-bg-green-500 tw-text-white tw-shadow-sm hover:tw-bg-green-400 focus-visible:tw-outline-green-500'
                : 'tw-text-green-600 tw-ring-1 tw-ring-green-200 tw-ring-inset hover:tw-ring-green-300 focus-visible:tw-outline-green-600',
            ]"
            >{{
              tier.is_coming ? "Coming soon" : "Get started today"
            }}</router-link
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CheckIcon } from "@heroicons/vue/20/solid";
import { useSeo } from "@/composables/useSeo";
import { useAnalytics } from "@/composables/useAnalytics";
import { useToastStore } from "@/stores/toast";
import { onMounted, ref } from "vue";

const toastStore = useToastStore();
const analytics = useAnalytics();
const baseUrl = import.meta.env.VITE_API_URL;

useSeo({
  title: "Plans",
  description: "Choose the right plan for you",
});

interface Tier {
  name: string;
  id: string;
  priceMonthly: string;
  features: string[];
  featured: boolean;
  is_coming: boolean;
  href: string;
}

// Initialize tiers with the correct type
const tiers = ref<Tier[]>([]);

/** Mid-funnel step: which plan a visitor picked, and which topic sent them. */
function onPlanSelect(tier: Tier) {
  analytics.planSelect({
    planId: tier.id,
    planName: tier.name,
    amount: tier.priceMonthly,
  });
}

onMounted(async () => {
  await subscriptionName();
});

const isLoading = ref(false);
const isSessionLoading = ref(false);
const v_session = ref("");

async function getSession() {
  isSessionLoading.value = true;

  const requestOptions = {
    method: "POST",
    credentials: "include" as RequestCredentials,
    headers: { "Content-Type": "application/json" },
  };

  try {
    const response = await fetch(`${baseUrl}/session`, requestOptions);

    if (response.status == 200) {
      const data = await response.json();
      v_session.value = data.r_uuid;
      isSessionLoading.value = false;
    } else {
      const vres = await response.text();
      const verror = JSON.parse(vres);
      if (verror.error.detail != undefined) {
        toastStore.error(verror.error.detail);
      } else {
        toastStore.error(verror.error.message);
      }
      isSessionLoading.value = false;
    }
  } catch (error) {
    isSessionLoading.value = false;
    console.log(error);
    if (error instanceof TypeError) {
      toastStore.error(error.message);
    }
  }
}

async function subscriptionName() {
  isLoading.value = true;
  const requestOptions = {
    method: "GET",
    credentials: "include" as RequestCredentials,
    headers: { "Content-Type": "application/json" },
  };
  try {
    const response = await fetch(
      `${baseUrl}/subscription?r_is_trial=1`,
      requestOptions
    );

    if (response.status == 200) {
      const data = await response.json();
      await getSession();
      for (let i = 0; i < data.length; i++) {
        const features = [];
        features.push(data[i].r_content_1);
        features.push(data[i].r_content_2);
        features.push(data[i].r_content_3);
        if (data[i].r_content_4 != null) {
          features.push(data[i].r_content_4);
        }
        tiers.value.push({
          name: data[i].r_subscription_lowername,
          id: data[i].r_id,
          priceMonthly: data[i].r_amount,
          features: features,
          featured: data[i].r_sorting === 2,
          is_coming: data[i].r_is_coming_value === 1,
          href: `/plans/${data[i].r_id}?data=${v_session.value}`,
        });
      }
    } else {
      tiers.value = [];
      const vres = await response.text();
      const verror = JSON.parse(vres);
      if (verror.error.detail != undefined) {
        toastStore.error(verror.error.detail);
      } else {
        toastStore.error(verror.error.message);
      }
    }
  } catch (error) {
    console.log(error);
    if (error instanceof TypeError) {
      toastStore.error(error.message);
    }
  } finally {
    isLoading.value = false;
  }
}
</script>
<route lang="yaml">
meta:
  layout: non-auth.vue
</route>
